import { mergeDeep } from "@apollo/client/utilities";
import { useEnhancedQuery } from "@uplift-ltd/apollo";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import { useRouter } from "next/router";
import React, { useCallback, useEffect } from "react";
import { EventType } from "__generated__/globalTypes";
import Avatar from "components/common/Avatar";
import { BackButton } from "components/common/Button";
import Container from "components/common/Container";
import CreditHoursBadge from "components/common/CreditHoursBadge/CreditHoursBadge";
import Heading from "components/common/Heading";
import HighlightedContent from "components/common/HighlightedContent";
import HtmlContent from "components/common/HtmlContent";
import Link from "components/common/Link";
import { StripeCheckoutStatus } from "components/common/PurchaseModal/StripeCheckoutStatus";
import CourseTitle from "components/courses/CourseTitle";
import { EventsList } from "components/events/Events";
import { EVENTS_URL } from "constants/urls";
import { replaceNonBreakingSpaces } from "helpers/strings";
import { currentUrlWithoutQueryString } from "helpers/urls";
import useWindowFocus from "helpers/windowFocus";
import { usePageMeta } from "hooks/usePageMeta";
import EventTimes from "./EventTimes";
import SidebarEnded from "./SidebarEnded";
import SidebarRegister from "./SidebarRegister";
import SidebarRegisterExternally from "./SidebarRegisterExternally";
import SidebarRegistered from "./SidebarRegistered";
import {
  EventInfoFragment as EventInfo,
  WebinarWordingQueryQuery as WebinarWordingQuery,
} from "./__generated__/queries";
import {
  CombinedEvent,
  EVENT_QUERY,
  EventQuery,
  EventQueryVariables,
  EVENTS_QUERY,
  EventsQuery,
  getEventVariables,
  useEvent,
  useEventForUser,
  WEBINAR_WORDING_QUERY,
} from "./queries";
import { formatStartDay, getResources, hasEventEnded } from "./utils";
import styles from "./Event.module.css";

export { EVENTS_QUERY, EVENT_QUERY, getEventVariables };

export type { EventQuery, EventQueryVariables, EventsQuery };

export interface EventProps {
  loading?: boolean;
}

export type EventParams = {
  slug: string;
};

type SidebarActionsProps = {
  event: CombinedEvent;
  startDay: string | null;
  webinarWordingData: WebinarWordingQuery | undefined;
};

const SidebarActions = ({ event, startDay, webinarWordingData }: SidebarActionsProps) => {
  const webinarWordingOption = webinarWordingData?.webinarWording?.webinarWording;
  const notes = (
    <p>
      {webinarWordingOption?.webinarWordingContent ??
        `For those unable to attend, a recording will be available within 5 days to view at your
      convenience. CE credits, (though not Live) will be provided after completing the posttest and
      evaluation. You will be notified via email when the recording is ready.`}
    </p>
  );

  if (event.product?.type !== "BUNDLE" && hasEventEnded(event)) {
    return (
      <>
        <SidebarEnded event={event} />
        {notes}
      </>
    );
  }

  if (event.isRegistered) {
    return (
      <>
        <SidebarRegistered event={event} startDay={startDay} />
        {notes}
      </>
    );
  }

  if (event.product?.type === "BUNDLE" && event.product.hasPurchased) {
    return (
      <div className={styles.sidebarRegisteredBundle}>
        You have registered for the individual webinars in this series
      </div>
    );
  }

  if (event.eventFields?.registerLink) {
    return <SidebarRegisterExternally event={event} />;
  }

  if (!event.eventFields?.isPurchasable) {
    return (
      <div className={styles.sidebarNotPurchasable}>
        {event.product?.type === "BUNDLE" ? (
          <>This series cannot be purchased at this time.</>
        ) : (
          <>
            This session is part of an entire series and cannot be purchased individually. If you
            would like to participate, please register for the series. Recordings of all sessions
            are provided to all registrants.
          </>
        )}
      </div>
    );
  }

  if (event.product) {
    return (
      <>
        <SidebarRegister
          event={event}
          product={event.product}
          title={webinarWordingOption?.webinarWordingTitle}
        />
        {notes}
      </>
    );
  }

  return null;
};

interface BundledWebinarProps {
  webinars: (EventInfo | null)[];
}

const BundledWebinars = ({ webinars }: BundledWebinarProps) => {
  const events: EventInfo[] = webinars.filter(notEmpty);

  return (
    <div>
      <Heading tag="h3">Webinars in this series</Heading>
      <div className={styles.bundledEventsContainer}>
        <EventsList events={events} layout="rows" />
      </div>
    </div>
  );
};

const Resources = ({ event }: { event: CombinedEvent }) => {
  const resources = getResources(event);

  if (!resources.length) {
    return null;
  }

  return (
    <div className={styles.resources}>
      <Heading tag="h3">Resources</Heading>
      <div className={styles.resourcesContainer}>
        {resources.map(resource => (
          <div key={resource.id}>
            <Link className={styles.resourceLink} href={resource.uri || ""}>
              {resource.title}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

interface ArticleProps {
  event: CombinedEvent;
  webinarWording: WebinarWordingQuery | undefined;
}

const Article = ({ event, webinarWording }: ArticleProps) => {
  const presenterImage = event.eventFields?.presenterImage;
  const eventStartDate = event.eventFields?.eventStartDateAndTime;
  const startDayString = formatStartDay(eventStartDate);

  return (
    <article className={styles.eventContainer}>
      <div className={styles.eventTextContainer}>
        <BackButton backToHref={EVENTS_URL}>Back to Webinars</BackButton>
        <CourseTitle>{event?.title}</CourseTitle>
        {presenterImage?.mediaItemUrl && (
          <Avatar
            textSize="lg"
            extended
            img={presenterImage}
            name={event.eventFields?.presenterName}
          />
        )}
        {event.eventFields?.video && (
          <HtmlContent className={styles.eventVideo}>{event.eventFields.video}</HtmlContent>
        )}
        {event.eventFields?.eventType !== EventType.WEBINAR_BUNDLE && (
          <div className={styles.eventDate}>
            <div>{startDayString}</div>
            <div>
              <EventTimes event={event} />
            </div>
          </div>
        )}
        <HtmlContent>{event.content}</HtmlContent>
        {(event.eventFields?.introText || event.eventFields?.presenterBio) && (
          <>
            <HtmlContent>{event.eventFields.introText}</HtmlContent>
            {event.eventFields.presenterBio && (
              <div className={styles.presenterInfo}>
                {presenterImage?.mediaItemUrl && (
                  <Avatar size="lg" img={presenterImage} name={event.eventFields?.presenterName} />
                )}
                <HtmlContent>
                  {replaceNonBreakingSpaces(event.eventFields.presenterBio)}
                </HtmlContent>
              </div>
            )}
          </>
        )}
        {event.eventFields?.bundledWebinars && (
          <BundledWebinars webinars={event.eventFields.bundledWebinars} />
        )}
        {event.eventFields?.eventObjectives && (
          <>
            <Heading tag="h3">Learning Objectives</Heading>
            <HtmlContent>{event.eventFields.eventObjectives}</HtmlContent>
          </>
        )}
        {event.eventFields?.eventObjectives && (
          <HighlightedContent>
            <Heading tag="h3">Webinar Outline</Heading>
            <HtmlContent>{event.eventFields.eventOutline}</HtmlContent>
          </HighlightedContent>
        )}
        {event.eventFields?.eventTechnicalInformation && (
          <HtmlContent>{event.eventFields.eventTechnicalInformation}</HtmlContent>
        )}
        {event.eventFields?.customBoardApprovals && (
          <HtmlContent>{event.eventFields.customBoardApprovals}</HtmlContent>
        )}
        <Resources event={event} />
      </div>
      <div className={styles.eventSidebarContainer}>
        <HighlightedContent className={styles.eventSidebar}>
          <CreditHoursBadge ceUnits={event.eventFields?.ceUnits || 0} />
          <SidebarActions
            event={event}
            startDay={startDayString}
            webinarWordingData={webinarWording}
          />
        </HighlightedContent>
      </div>
    </article>
  );
};

const Event = ({ loading: pageLoading }: EventProps) => {
  const router = useRouter();
  const { slug } = router.query;
  const { data, loading: eventLoading } = useEvent({ slug });
  const { data: userData, loading: eventForUserLoading, refetch } = useEventForUser({ slug });
  const { data: webinarWording, loading: webinarWordingLoading } =
    useEnhancedQuery<WebinarWordingQuery>(WEBINAR_WORDING_QUERY);

  const isLoading = pageLoading || eventLoading || eventForUserLoading || webinarWordingLoading;
  const eventCore = data?.event;
  const eventForUser = userData?.event;
  const event = mergeDeep(eventCore, eventForUser);

  useWindowFocus({
    onFocus: () => {
      refetch();
    },
  });

  const refetchCallback = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    setInterval(refetchCallback, 2 * 60 * 1000);
  }, [refetchCallback]);

  usePageMeta(data?.event?.title, data?.event?.seo);

  return (
    <Container>
      <StripeCheckoutStatus
        onSuccessRedirectUrl={currentUrlWithoutQueryString}
        key={router.asPath}
        loading={isLoading}
      >
        {event && <Article event={event} webinarWording={webinarWording} />}
      </StripeCheckoutStatus>
    </Container>
  );
};

export default Event;
